import { createRouter, createWebHistory } from "vue-router"
import { adminLoggedIn, checkAuth, nLoggedIn, checkNAuth, adminPermissions, onSessionExpired } from "./store.js"
import { toast } from "./utils.js"

// Lazy-loaded views — Vite emits one chunk per route, so a first-time public
// form visitor doesn't download the admin panel.
export const router = createRouter({
  history: createWebHistory(),
  routes: [
    // public
    { path: "/",                          component: () => import("./views/Home.vue") },
    { path: "/:formId(child|women|men)",  component: () => import("./views/FormView.vue"), props: true, meta: { publicForm: true } },
    { path: "/invite/verify",             component: () => import("./views/InviteVerify.vue") },
    { path: "/invite/:token",             component: () => import("./views/InviteForm.vue") },
    { path: "/s/:token",                  component: () => import("./views/SubmissionReview.vue") },
    { path: "/success",                   component: () => import("./views/Success.vue") },
    { path: "/auth/callback",             component: () => import("./views/OidcCallback.vue") },
    // admin
    { path: "/admin",                     component: () => import("./views/AdminLogin.vue") },
    { path: "/admin/submissions",         component: () => import("./views/AdminDashboard.vue"), meta: { requiresAdmin: true, perm: "perm_submissions" } },
    { path: "/admin/submissions/:id",     component: () => import("./views/AdminSubmission.vue"), meta: { requiresAdmin: true, perm: "perm_submissions" } },
    { path: "/admin/invitations",         component: () => import("./views/AdminInvitations.vue"), meta: { requiresAdmin: true, perm: "perm_invitations" } },
    { path: "/admin/nutritionists",       component: () => import("./views/AdminNutritionists.vue"), meta: { requiresAdmin: true, perm: "perm_nutritionists" } },
    { path: "/admin/zitadel-users",       component: () => import("./views/AdminZitadelUsers.vue"), meta: { requiresAdmin: true, perm: "perm_nutritionists" } },
    { path: "/admin/settings",            component: () => import("./views/AdminSettings.vue"), meta: { requiresAdmin: true, perm: "perm_settings" } },
    { path: "/admin/roles",               component: () => import("./views/AdminRoles.vue"), meta: { requiresAdmin: true, perm: "perm_settings" } },
    { path: "/admin/trash",               component: () => import("./views/AdminTrash.vue"), meta: { requiresAdmin: true, perm: "perm_trash" } },
    { path: "/admin/monitoring",          component: () => import("./views/AdminMonitoring.vue"), meta: { requiresAdmin: true, superAdmin: true } },
    { path: "/admin/questions",           component: () => import("./views/QuestionsIndex.vue"), meta: { requiresAdmin: true, perm: "perm_questions" } },
    { path: "/admin/questions/:formId",   component: () => import("./views/QuestionsEdit.vue"), meta: { requiresAdmin: true, perm: "perm_questions" } },
    // cabinet
    { path: "/n",                         component: () => import("./views/Login.vue") },
    { path: "/n/dashboard",               component: () => import("./views/Dashboard.vue"), meta: { requiresN: true } },
    { path: "/n/submissions/:token",      component: () => import("./views/NutritionistSubmission.vue"), meta: { requiresN: true } },
    { path: "/n/clients/:id",             component: () => import("./views/ClientDetail.vue"), meta: { requiresN: true } },
    { path: "/n/clients/:id/fill/:formId(child|women|men)", component: () => import("./views/NutritionistFillForm.vue"), meta: { requiresN: true } },
    { path: "/n/clients/:id/compare",     component: () => import("./views/SubmissionCompare.vue"), meta: { requiresN: true } },
    // 404
    { path: "/:pathMatch(.*)*",           component: () => import("./views/NotFound.vue") },
  ],
  // Restore the previous scroll position on back/forward; top otherwise.
  scrollBehavior(to, from, savedPosition) { return savedPosition || { top: 0 } },
})

let authChecked = false
let nAuthChecked = false
router.beforeEach(async (to) => {
  if (!authChecked)  { await checkAuth();  authChecked = true }
  if (!nAuthChecked) { await checkNAuth(); nAuthChecked = true }

  if (to.meta.requiresAdmin) {
    if (adminLoggedIn.value) return true
    if (to.meta.superAdmin) return nLoggedIn.value ? "/n/dashboard" : "/admin"
    const perm = to.meta.perm
    if (perm && adminPermissions.value[perm]) return true
    if (!perm && Object.values(adminPermissions.value).some(Boolean)) return true
    if (nLoggedIn.value) return "/n/dashboard"
    return "/admin"
  }
  if (to.meta.requiresN && !nLoggedIn.value) return "/n"

  if (to.meta.publicForm && nLoggedIn.value && !adminLoggedIn.value) {
    toast("Чтобы заполнить анкету, выберите клиента в кабинете", "info")
    return "/n/dashboard"
  }
})

// When a token refresh fails mid-session, re-check server auth and redirect off
// any route the user can no longer access (the one-shot flags are reset so the
// guard re-evaluates on the next navigation too).
onSessionExpired(async () => {
  authChecked = false
  nAuthChecked = false
  await Promise.all([checkAuth(), checkNAuth()])
  authChecked = true
  nAuthChecked = true
  const to = router.currentRoute.value
  const hasAdmin = adminLoggedIn.value || Object.values(adminPermissions.value).some(Boolean)
  if (to.meta.requiresAdmin && !hasAdmin) {
    toast("Сессия истекла — войдите снова", "info")
    router.replace(nLoggedIn.value ? "/n/dashboard" : "/admin")
  } else if (to.meta.requiresN && !nLoggedIn.value) {
    toast("Сессия истекла — войдите снова", "info")
    router.replace("/n")
  }
})
